//-- Copyright (c) 2021 TMT Insights Group, LLC
import { DashboardConfig } from '@/__configs/config_types';
import axios from '@/_utils/axios';
import { PreBuiltGraphs, buildGraphsOrderDefault, defaultGraphs } from '@/components/Dashboard/DefaultGraphs';
import { LoadingStatuses } from '@/constants/constants';
import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { CHART_STATUS_LABELS, parseDashboardStats } from '../../components/Dashboard/chartUtils';
import { graphList, parseFns } from '../selectors/configSelectors';
import { OGDashboardData, DailyReportResponse, DashboardState, TotalJobsCompletedByType, DashboardData } from './slice_types';
import { RootState } from '@/redux/store';
import { PLACEHOLDER_CHART } from '@/components/Dashboard/Widgets/WidgetFrame';
import { selectJobStages } from '../selectors/constantsSelectors';

const initialState: DashboardState = {
  data: {} as DashboardData,
  ogData: {} as OGDashboardData,
  dailyReportData: {
    data: [],
    pagination: {
      page_size: 10,
      page: 0,
      total: 0
    },
    message: '',
    success: false
  },
  generatedReportData: {
    data: [],
    pagination: {
      page_size: 10,
      page: 0,
      total: 0
    },
    message: '',
    success: false
  },
  missingComponents: [],
  currentCycle: 'All',
  status: LoadingStatuses.pending,
  dailyReportStatus: LoadingStatuses.pending,
  generatedReportStatus: LoadingStatuses.pending,
  config: {} as DashboardConfig,
  graphs: {
    status: {
      condensed: false,
      editable: false,
      saveChanges: false
    },
    recentTasks: {
      all: [],
      me: []
    },
    readOnly: {
      orderBuildType: 'default',
      list: [],
      order: [],
      statusCards: []
    },
    editable: {
      orderBuildType: 'default',
      list: [],
      order: [],
      statusCards: []
    }
  }
};

export const getDashboardData = createAsyncThunk('dataTable/getDashboardData', async (_, { getState, rejectWithValue }) => {
  try {
    const payloadData = {
      jobStats: {},
      outstandingAssets: {}
    };
    const rootState = getState() as RootState;
    const dashboardState = rootState.dashboard;
    const jobStages = selectJobStages(rootState);
    const currentCycle = dashboardState.currentCycle;

    const jobStatsResponse = await axios.get('/core/jobs/stats/', {
      params: typeof currentCycle === 'object' && currentCycle.queryParams ? currentCycle.queryParams() : undefined,
    });
    if (!jobStatsResponse.data?.success) throw rejectWithValue(jobStatsResponse);
    else payloadData.jobStats = jobStatsResponse.data;

    // if 'oustanding-assets' graph isn't in the enabledGraphs list or default graphs
    // don't make the call or throw an error
    if (
      (dashboardState.config.graphList || []).map((graph) => graph.id).includes(PreBuiltGraphs.OUTSTANDING_ASSETS) ||
      defaultGraphs.map((graph) => graph.id).includes(PreBuiltGraphs.OUTSTANDING_ASSETS)
    ) {
      const oustandingAssetResponse = await axios.get('/core/outstanding-assets/stats/');
      if (!oustandingAssetResponse.data?.success) {
        console.error('No data for Oustanding Assets: ', oustandingAssetResponse);
      } else {
        payloadData.outstandingAssets = oustandingAssetResponse.data;
      }
    }

    return {
      payloadData: payloadData as OGDashboardData,
      dashboardPrefs: (getState() as RootState).auth.preferences?.dashboard,
      jobStages: Object.keys(jobStages),
    };
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const getDailyReportData = createAsyncThunk('dataTable/getDailyReportData', async (params: { pageSize: number; page: number }) => {
  const response = await axios.post(
    '/reporting/daily_reports/',
    {
      order_on: 'created_at',
      direction: 'desc'
    },
    {
      params: {
        page_size: params.pageSize,
        page_num: params.page
      }
    }
  );

  return response.data;
});

export const getDailyReportDataNew = createAsyncThunk('dataTable/getDailyReportDataNew', async (params: { pageSize: number; page: number }) => {
  const response = await axios.post(
    '/reporting/',
    {
      order_on: 'created_at',
      direction: 'desc',
      tag_filter: ['daily']
    },
    {
      params: {
        page_size: params.pageSize,
        page_num: params.page
      }
    }
  );

  return response.data;
});

export const getGeneratedReportData = createAsyncThunk('dataTable/getGeneratedReportData', async (params: { pageSize: number; page: number }) => {
  const response = await axios.post(
    '/reporting/',
    {
      order_on: 'created_at',
      direction: 'desc',
      tag_filter: ['custom']
    },
    {
      params: {
        page_size: params.pageSize,
        page_num: params.page
      }
    }
  );

  return response.data;
});

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    setCurrentCycle: (state, { payload }) => {
      state.currentCycle = payload;
    },
    setDashboardConfig: (state, { payload }) => {
      state.config = payload;
    },
    saveGraphChanges: (state: DashboardState) => {
      // filter out empty rows and placeholders
      const filteredOrder = state.graphs.editable.order.filter((row) => {
        if (row.length === 1 && row[0] === PLACEHOLDER_CHART) {
          return false;
        }

        return row.length > 0;
      });
      state.graphs.readOnly = {
        ...state.graphs.editable,
        order: filteredOrder
      };
      state.graphs.editable.order = filteredOrder;
      state.graphs.status.saveChanges = true;
      state.graphs.status.editable = false;
    },
    setGraphsStatusCondensed: (state: DashboardState, { payload }: { payload: boolean }) => {
      state.graphs.status.condensed = payload;
    },
    cancelGraphChanges: (state: DashboardState) => {
      state.graphs.editable = state.graphs.readOnly;
      state.graphs.status.saveChanges = false;
      state.graphs.status.editable = false;
      const { data, parsedJobsByType, tasksProgressMonthlyAvg } = parseDashboardStats(state.ogData.jobStats, state.graphs.readOnly, state.config);
      state.data = {
        ...data,
        tasksProgressMonthlyAvg: tasksProgressMonthlyAvg,
        totalJobsCompletedByType: parsedJobsByType as TotalJobsCompletedByType
      };
    },
    openGraphs: (state) => {
      state.graphs.status.editable = true;
    },
    setGraphsEditable: (state, { payload }: { payload: DashboardState['graphs']['editable'] }) => {
      state.graphs.editable = payload;
      const { data, parsedJobsByType, tasksProgressMonthlyAvg } = parseDashboardStats(state.ogData.jobStats, payload, state.config);
      state.data = {
        ...data,
        tasksProgressMonthlyAvg: tasksProgressMonthlyAvg,
        totalJobsCompletedByType: parsedJobsByType as TotalJobsCompletedByType
      };
    },
    setGraphsReadOnly: (state, { payload }: { payload: DashboardState['graphs']['readOnly'] }) => {
      state.graphs.readOnly = payload;
    },
    setGraphsRecentTasks: (state, { payload }: { payload: DashboardState['graphs']['recentTasks'] }) => {
      state.graphs.recentTasks = payload;
    },
    setDashboardStatus: (state, { payload }: { payload: LoadingStatuses }) => {
      state.status = payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getDailyReportData.pending, (state: DashboardState) => {
      state.dailyReportStatus = LoadingStatuses.pending;
    });
    builder.addCase(getDailyReportData.rejected, (state: DashboardState) => {
      state.dailyReportStatus = LoadingStatuses.rejected;
    });
    builder.addCase(getDailyReportData.fulfilled, (state: DashboardState, { payload }: PayloadAction<DailyReportResponse>) => {
      state.dailyReportData = payload;
      state.dailyReportStatus = LoadingStatuses.fulfilled;
    });
    builder.addCase(getDailyReportDataNew.pending, (state: DashboardState) => {
      state.dailyReportStatus = LoadingStatuses.pending;
    });
    builder.addCase(getDailyReportDataNew.rejected, (state: DashboardState) => {
      state.dailyReportStatus = LoadingStatuses.rejected;
    });
    builder.addCase(getDailyReportDataNew.fulfilled, (state: DashboardState, { payload }: PayloadAction<DailyReportResponse>) => {
      state.dailyReportData = payload;
      state.dailyReportStatus = LoadingStatuses.fulfilled;
    });
    builder.addCase(getGeneratedReportData.pending, (state: DashboardState) => {
      state.generatedReportStatus = LoadingStatuses.pending;
    });
    builder.addCase(getGeneratedReportData.rejected, (state: DashboardState) => {
      state.generatedReportStatus = LoadingStatuses.rejected;
    });
    builder.addCase(getGeneratedReportData.fulfilled, (state: DashboardState, { payload }: PayloadAction<DailyReportResponse>) => {
      state.generatedReportData = payload;
      state.generatedReportStatus = LoadingStatuses.fulfilled;
    });
    builder.addCase(getDashboardData.pending, (state: DashboardState) => {
      state.status = LoadingStatuses.pending;
    });
    builder.addCase(getDashboardData.rejected, (state: DashboardState) => {
      state.status = LoadingStatuses.rejected;
    });
    builder.addCase(
      getDashboardData.fulfilled,
      (
        state: DashboardState,
        {
          payload
        }: PayloadAction<{
          payloadData: OGDashboardData;
          dashboardPrefs: DashboardState['graphs']['readOnly'];
          jobStages: string[];
        }>
      ) => {
        state.status = LoadingStatuses.fulfilled;
        const { payloadData, dashboardPrefs, jobStages } = payload;
        const parseDataFn = parseFns && parseFns.dashboardConfig ? parseFns.dashboardConfig.parseDataFn : undefined;
        state.ogData = payloadData;

        // build graphs info
        const savedPreferences = dashboardPrefs;

        // if sorted graph ids from saved preferences and graphList (created from config) are the same
        const graphIdsMatch =
          JSON.stringify(savedPreferences?.list.map((graph) => graph.id).sort()) === JSON.stringify(graphList.map((graph) => graph.id).sort());

        const preferencesMatchDefaults =
          savedPreferences?.list?.length === graphList.length &&
          savedPreferences?.list?.every((item, index) => {
            return JSON.stringify(item) === JSON.stringify(graphList[index]);
          });

        // if saved preferences for the graph order exist, the config allows customization, and the default config's graph id list are the same
        if (
          savedPreferences &&
          state.config.customizeDashboard &&
          graphIdsMatch &&
          (!preferencesMatchDefaults || savedPreferences.orderBuildType === 'custom')
        ) {
          // load saved preferences data and set it as the redux state
          const prefs = savedPreferences?.statusCards
            ? savedPreferences
            : {
                ...savedPreferences,
                statusCards: state.config?.statusCards ? state.config.statusCards : CHART_STATUS_LABELS
              };

          state.graphs.readOnly = prefs;
          state.graphs.editable = prefs;
        } else {
          // if the saved preferences sorted graph ids are not the same as the default graph id list, reset to config defaults
          if (!graphIdsMatch) console.warn('Some graphs have been added or removed from the default list. Resetting to default.');

          const defaults = {
            list: graphList,
            order: buildGraphsOrderDefault(graphList),
            orderBuildType: 'default' as DashboardState['graphs']['readOnly']['orderBuildType'],
            statusCards: state.config?.statusCards ? state.config.statusCards : CHART_STATUS_LABELS
          };

          state.graphs.readOnly = defaults;
          state.graphs.editable = defaults;
        }

        if (parseDataFn) {
          const parsedClientData = parseDataFn(payloadData.jobStats);
          const { data, parsedJobsByType, tasksProgressMonthlyAvg } = parseDashboardStats(parsedClientData, state.graphs.readOnly, state.config, jobStages);

          if (parsedJobsByType && Object.keys(parsedJobsByType).length === 0)
            console.error("No data for Dashboard data's total_jobs_completed_by_type: ", data);

          state.data = {
            ...data,
            tasksProgressMonthlyAvg: tasksProgressMonthlyAvg,
            totalJobsCompletedByType: parsedJobsByType as TotalJobsCompletedByType,
            total: parsedClientData.data.total,
            outstandingAssets: payloadData.outstandingAssets.data
          };
        } else {
          const { data, parsedJobsByType, tasksProgressMonthlyAvg } = parseDashboardStats(payloadData.jobStats, state.graphs.readOnly, state.config, jobStages);

          if (parsedJobsByType && Object.keys(parsedJobsByType).length === 0)
            console.error("No data for Dashboard data's total_jobs_completed_by_type: ", data);

          state.data = {
            ...data,
            tasksProgressMonthlyAvg: tasksProgressMonthlyAvg,
            totalJobsCompletedByType: parsedJobsByType as TotalJobsCompletedByType,
            total: payloadData.jobStats.data.total,
            outstandingAssets: payloadData.outstandingAssets.data
          };
        }
      }
    );
  }
});

export const {
  setCurrentCycle,
  setGraphsStatusCondensed,
  setDashboardConfig,
  setGraphsEditable,
  setGraphsReadOnly,
  openGraphs,
  saveGraphChanges,
  cancelGraphChanges,
  setGraphsRecentTasks,
  setDashboardStatus
} = dashboardSlice.actions;

export default dashboardSlice.reducer;
